import { Component, OnInit } from '@angular/core';
import { LayoutService } from '@common/services/layout.service';
import { NumberInputComponent } from '@common/UI/number-input/number-input.component';
import { SortableExtractor } from '@common/UI/sortable/sortable.component';
import { Filterable } from '@common/utilities/Filterable';
import { Lookup } from '@common/utilities/Lookup';
import { commit, main } from '@core/operators';
import { ApiService } from '@core/services/api.service';
import { DataService, DiscountDetailRecord, DiscountRecord, DiscountState, discountState, GoodRecord, PartnerRecord, UserRecord, ProductUnit } from '@core/services/data.service';
import { PartnersService } from '@core/services/partners.service';
import { SynchronizationService } from '@core/services/synchronization.service';
import { UsersService } from '@core/services/users.service';
import { Multilingual } from '@core/utilities/multilingual';
import { MessageBoxService } from '@routes/common/message-box.service';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ModalService } from '@common/services/modal.service';
import { CalendarComponent } from '@common/UI/calendar/calendar.component';
import { TagsService } from '@core/services/tags.service';
import { ConfigsService } from '@core/services/configs.service';
import { match } from 'minimatch';
import { CurrenciesService } from '@core/services/currencies.service';
import { ViewerComponent } from './viewer/viewer.component';

const lu = {
  goods: new Lookup<GoodRecord>(i => i.ID),
};

interface Group {
  SKU: string;
  Color: string;
  Unit: ProductUnit;
  ImportUnitPrice: number;
  ExportUnitPrice: number;

  records: DiscountDetailRecord[];
}

interface PartnerEx extends PartnerRecord {

  ConfirmedCount: number;
  EditableCount: number;
  ImportDiscountVndAmount: number;
  ImportDiscountCnyAmount: number;
  
  ExportDiscountVndAmount: number;
  ExportDiscountCnyAmount: number;

  records: DiscountRecord[];
}

interface Vendor {
  ID: number;
  Name: string;
  LengthCount: number;
  WeightCount: number;
  LengthQuantity: number;
  WeightQuantity: number;

  records: GoodRecord[];
}

@Component({
  templateUrl: './discounts.component.html',
  styleUrls: ['./discounts.component.scss']
})
export class DiscountsComponent implements OnInit {

  constructor(
    private layout: LayoutService,
    private ds: DataService,
    private modal: ModalService,
    private api: ApiService,
    private sync: SynchronizationService,
    private mb: MessageBoxService,
    private _partners: PartnersService,
    private _tags: TagsService,
    private _users: UsersService,
    private currencies: CurrenciesService,
    public configs: ConfigsService    
  ) { }

  public get loading(): boolean {
    return !this.sync.ready(CurrenciesService, UsersService, PartnersService, DiscountsComponent, TagsService, ApiService)
  }

  public discounts = new Filterable<DiscountRecord>({ grouper: i => [{ name: "Nguồn hàng", option: this.vendor(i).Name, description: 'K' + i.VendorID }] });

  public goods = new Filterable<GoodRecord>({
    extractor: i => [i.SKU + "m" + i.Color + "d" + i.ActualQuantity, i.SKU + 'd' + i.ActualQuantity],
    grouper: i => [{ name: 'Đơn nhập', option: i.ImportInvoiceTag }]
  });

  public partners = new Filterable<PartnerEx>({
    extractor: i => [i.Name, 'K' + i.ID],
    grouper: i => [
      { name: 'Khu vực', option: i.Address },
      { name: 'Đơn xong', option: i.ConfirmedCount > 0 ? 'Có đơn' : 'Không có' },
      { name: 'Đơn chờ', option: i.EditableCount > 0 ? 'Có đơn' : 'Không có' }
    ]
  });

  public vendors = new Filterable<Vendor>({ extractor: i => [i.Name, 'K' + i.ID] });


  public all: PartnerEx = {
    ID: 0,
    Name: Multilingual.getText('Tất cả đơn trừ lỗi'),
    Address: '',
    Email: '',
    Phone: '',
    DealerID: 0,
    ExportPolicyID: 0,
    ImportPolicyID: 0,

    ConfirmedCount: 0,
    EditableCount: 0,

    ImportDiscountCnyAmount: 0,
    ImportDiscountVndAmount: 0,

    ExportDiscountCnyAmount: 0,
    ExportDiscountVndAmount: 0,

    records: []
  };

  public selected: { discount: DiscountRecord, details: Group[], partner: PartnerRecord, vendor: Vendor } = { discount: null, partner: null, vendor: null, details: [] };
  private edited: boolean = false;

  public edit() {
    let ref = this.modal.show(CalendarComponent, { state: { selected: new Date(this.selected.discount.Stamp) } });
    ref.content.change.subscribe(value => {
      this.selected.discount.Stamp = value.getTime();
      ref.hide();
      this.edited = true;
    });
  }

  private synchronize() {

    this.selected.discount = null;
    this.selected.partner = null;
    this.discounts.source = [];

    let discounts: DiscountRecord[] = [];
    let partners: PartnerRecord[] = [];

    this.sync.with(DiscountsComponent).repeat(
      () => this.ds.discounts('pendings').pipe(map(data => data ? commit<boolean>(() => discounts = data).return(true) : false)),
      () => this.ds.partners('discountable-list').pipe(map(data => data ? commit<boolean>(() => partners = data).return(true) : false))
    );
    
    this.sync.when(UsersService, PartnersService, DiscountsComponent, TagsService, DiscountsComponent).commit(() => {

      this.partners.source = partners.filter(i => i.ID > 3).map<PartnerEx>(partner => {

        let matches = discounts.filter(i => i.CustomerID == partner.ID);

        return {
          ...partner,

          ConfirmedCount: matches.count(i => i.State == DiscountState.Confirmed),
          EditableCount: matches.count(i => i.State == DiscountState.Editable),

          ImportDiscountVndAmount: matches.sum(i => i.ImportDiscountSymbol == 'VND' ? i.ImportDiscountAmount : 0),
          ImportDiscountCnyAmount: matches.sum(i => i.ImportDiscountSymbol == 'CNY' ? i.ImportDiscountAmount : 0),

          ExportDiscountVndAmount: matches.sum(i => i.ExportDiscountSymbol == 'VND' ? i.ExportDiscountAmount : 0),
          ExportDiscountCnyAmount: matches.sum(i => i.ExportDiscountSymbol == 'CNY' ? i.ExportDiscountAmount : 0),

          records: matches
        };

      });

      this.all = {
        ...this.all,
        ConfirmedCount: discounts.count(i => i.State == DiscountState.Confirmed),
        EditableCount: discounts.count(i => i.State == DiscountState.Editable),

        ImportDiscountVndAmount: discounts.sum(i => i.ImportDiscountSymbol == 'VND' ? i.ImportDiscountAmount : 0),
        ImportDiscountCnyAmount: discounts.sum(i => i.ImportDiscountSymbol == 'CNY' ? i.ImportDiscountAmount : 0),

        ExportDiscountVndAmount: discounts.sum(i => i.ExportDiscountSymbol == 'VND' ? i.ExportDiscountAmount : 0),
        ExportDiscountCnyAmount: discounts.sum(i => i.ExportDiscountSymbol == 'CNY' ? i.ExportDiscountAmount : 0),

        records: discounts,
      }
    });
  }

  ngOnInit(): void {

    main(() => {
      this.layout.media('footer', 'none');
      this.layout.title = Multilingual.getText("Khấu trừ hàng lỗi");
    });

    this.synchronize();
  }

  public get editable(): boolean {
    return this.selected.discount && this.selected.discount.State == DiscountState.Editable;
  }

  public get creatable(): boolean {
    return !this.selected.discount;
  }

  public get savable(): boolean {
    return this.selected.discount != null && this.selected.discount.Details.length > 0 && this.selected.discount.Details.count(i => i.ImportDiscountQuantity < 0 || i.ExportDiscountQuantity < 0 || i.ImportDiscountQuantity + i.ExportDiscountQuantity <= 0) < 1 && this.edited;
  }

  public get confirmable(): boolean {
    return this.selected.discount != null && this.selected.discount.State == DiscountState.Editable && this.selected.discount.Details.length > 0 && this.selected.discount.Details.count(i => i.ImportDiscountQuantity < 0 || i.ExportDiscountQuantity < 0 || i.ImportDiscountQuantity + i.ExportDiscountQuantity <= 0) < 1 && !this.edited;
  }

  public get cancelable(): boolean {
    return this.selected.discount != null && this.selected.discount.State == DiscountState.Editable && ((this.selected.discount.Details.length > 0 && !this.edited) || this.selected.discount.ID == 0 || (this.selected.discount.Details.length < 1 && this.edited));
  }

  public user(id: number): UserRecord {
    return this._users.get(id);
  }

  public partner(discount: DiscountRecord): PartnerRecord {
    return this._partners.get(discount.CustomerID);
  }

  public vendor(discount: DiscountRecord): PartnerRecord {
    return this._partners.get(discount.VendorID);
  }

  public source(tag: string): string {
    let t = this._tags.get(tag);
    if (!t) return '';

    let p = this._partners.get(t.PartnerID);
    return p ? p.Name : '';
  }

  public state = discountState;

  public distinct(records: any[], field: string): any[] {
    return records.map(i => i[field]).distinct();
  }
 
  public statistics(field: string, groups?: Group[]): number {

    if (!groups) groups = this.selected.details;

    switch (field) {
      case "SKU": return groups.group(i => i.SKU).length;
      case "Color": return groups.group(i => i.SKU + "/" + i.Color).length;
      case "Quantity": return groups.sum(i => i.records.length);
      case "ReducedLength": return groups.sum(i => i.records.sum(x => x.ActualQuantity - x.ExportQuoteQuantity));
      case 'ImportDiscountAmount': return groups.sum(i => i.records.sum(x => x.ImportDiscountQuantity * x.ImportUnitPrice));
      case 'ExportDiscountAmount': return groups.sum(i => i.records.sum(x => x.ExportDiscountQuantity * x.ExportUnitPrice));
      default: return groups.sum(i => i.records.sum(x => x[field]));
    }
  }

  public get lengthCount(): number {
    return this.selected.discount.Details.count(i => i.Unit == ProductUnit.Meter);
  }

  public get weightCount(): number {
    return this.selected.discount.Details.count(i => i.Unit == ProductUnit.Kilogram);
  }

  public sum(records: any[], field: string): number {
    return records.sum(i => i[field]);
  }

  public extractor(field: string): SortableExtractor<DiscountRecord> {
    return {
      extract: i => {
        switch (field) {
          case 'SKUCount': return this.distinct(i.Details, 'SKU').length;
          case 'State': return this.state(i);
          case 'Count': return i.Details.length;
          case 'Partner': return this.partner(i).Name;
          case 'Vendor': return this.vendor(i).Name;
          case 'ImportDiscountAmount': return i.ImportDiscountAmount * this.currencies.get(i.ImportDiscountSymbol).Conversion;
          default: return i[field];
        }
      }
    }
  }

  public partnerExtractor(field: string): SortableExtractor<PartnerEx> {
    return {
      extract: i => {
        switch (field) {
          case 'ImportDiscountAmount': return i.ImportDiscountVndAmount + i.ImportDiscountCnyAmount * this.currencies.get('CNY').Conversion;
          case 'ExportDiscountAmount': return i.ExportDiscountVndAmount + i.ExportDiscountCnyAmount * this.currencies.get('CNY').Conversion;
          default: return i[field];
        }
      }
    }
  }

  public select(partner: PartnerEx) {

    this.selected.partner = partner ? partner : null;
    this.discounts.source = partner ? partner.records : [];
  }

  public setLength(detail: DiscountDetailRecord, type: 'import' | 'export', input: NumberInputComponent) {

    if (isNaN(input.value)) input.value = 0;
    input.value = Math.max(0, Math.min(type == 'import' ? detail.ImportQuoteQuantity : detail.ExportQuoteQuantity, input.value));

    switch (type) {
      case 'import':

        detail.ImportDiscountQuantity = input.value;

        if (detail.ExportDiscountQuantity == 0 || detail.ExportDiscountQuantity > detail.ImportDiscountQuantity) {
          detail.ExportDiscountQuantity = detail.ImportDiscountQuantity;
        }

        break;

      case 'export':

        detail.ExportDiscountQuantity = input.value;
        break;
    }

    this.edited = true;
  }

  public open(discount: DiscountRecord) {

    if (discount.State == DiscountState.Confirmed) {
      this.modal.show(ViewerComponent, {
        state: {
          discount: discount, mode: 'full' } });
      return;
    }


    this.sync.with(DiscountsComponent).repeat(() => this.ds.goods("customer-resolvable", { CustomerID: this.selected.partner.ID }).pipe(map(goods => {

      if (goods) {

        this.vendors.source = goods.group(i => i.VendorID).map<Vendor>(i => ({
          ID: i.key as number,
          Name: this._partners.get(i.key as number).Name,
          LengthCount: i.items.count(i => i.Unit == ProductUnit.Meter),
          WeightCount: i.items.count(i => i.Unit == ProductUnit.Kilogram),
          LengthQuantity: i.items.sum(i => i.Unit == ProductUnit.Meter ? i.ActualQuantity : 0),
          WeightQuantity: i.items.sum(i => i.Unit == ProductUnit.Kilogram ? i.ActualQuantity : 0),

          records: i.items
        }));

        lu.goods.source = goods.filter(i => i.DiscountID < 1 && !i.Returned).order(i => i.ExportStamp).order(i => [i.SKU, i.Color, i.ActualQuantity, i.ExportQuoteQuantity, i.ImportUnitPrice, i.ExportUnitPrice].join('/'));
        this.goods.source = [];

        return true;
      }

      return false;
    })));

    this.sync.when(DiscountsComponent).commit(() => {

      this.selected.discount = { ...discount, Details: discount.Details.map(i => ({ ...i })) };

      this.selected.details = this.selected.discount.Details.group(i => [i.SKU, i.Color, i.Unit, i.ImportUnitPrice, i.ExportUnitPrice].join('/')).map<Group>(i => ({
        SKU: i.items[0].SKU,
        Color: i.items[0].Color,
        Unit: i.items[0].Unit,
        ImportUnitPrice: i.items[0].ImportUnitPrice,
        ExportUnitPrice: i.items[0].ExportUnitPrice,
        records: i.items
      }));

      if (discount.ID > 0) {
        this.toggle(this.vendors.source.first(i => i.ID == discount.VendorID));
      }
      else {
        if (this.vendors.source.length == 1) {
          this.toggle(this.vendors.source[0]);
        }
      }
    });    
  }

  public toggle(vendor: Vendor) {

    this.selected.vendor = vendor;
    this.selected.discount.VendorID = vendor ? vendor.ID : 0;
    this.goods.source = vendor ? vendor.records : [];    
  }

  public close() {

    if (this.edited) {

      this.mb.confirms('discourage', "Đơn trừ hàng lỗi có các thay đổi chưa lưu lại. Đóng đơn sẽ không lưu lại các thay đổi đó. Bạn có muốn đóng đơn?",
        () => {
          this.edited = false;
          this.synchronize();
          return new Observable<boolean>(subscriber => subscriber.next(true));
        });
    }
    else {
      this.synchronize();
    }
  }

  public create() {

    this.open({
      ID: 0,

      VendorID: 0,
      CustomerID: this.selected.partner.ID,

      State: DiscountState.Editable,

      CreatorID: this.api.user.ID,
      UpdatorID: this.api.user.ID,
      
      FirstStamp: new Date().getTime(),
      LastStamp: new Date().getTime(),

      Stamp: new Date().getTime(),

      Details: [],

      ImportDiscountAmount: 0,
      ImportDiscountIcon: null,
      ImportDiscountSymbol: null,

      ExportDiscountAmount: 0,
      ExportDiscountIcon: null,
      ExportDiscountSymbol: null
    });
  }

  public add(good: GoodRecord) {

    let detail: Group = this.selected.details.first(i => i.SKU == good.SKU && i.Color == good.Color && i.ImportUnitPrice == good.ImportUnitPrice && i.ExportUnitPrice == good.ExportUnitPrice, {

      SKU: good.SKU,
      Color: good.Color,
      Unit: good.Unit,
      ImportUnitPrice: good.ImportUnitPrice,
      ExportUnitPrice: good.ExportUnitPrice,
      records: []
    });

    let record: DiscountDetailRecord = {
      ID: good.ID,
      SKU: good.SKU,
      Color: good.Color,
      Unit: good.Unit,
      ActualQuantity: good.ActualQuantity,
      ImportQuoteQuantity: good.ImportQuoteQuantity,
      ExportQuoteQuantity: good.ExportQuoteQuantity,

      ImportUnitPrice: good.ImportUnitPrice,
      ExportUnitPrice: good.ExportUnitPrice,

      ExportDiscountQuantity: 0,
      ImportDiscountQuantity: 0,
    };

    if (this.selected.discount.Details.length < 1) {

      this.selected.discount.ImportDiscountSymbol = good.ImportSymbol;
      this.selected.discount.ExportDiscountSymbol = good.ExportSymbol;

      this.selected.discount.ImportDiscountIcon = this.currencies.get(good.ImportSymbol).Icon;
      this.selected.discount.ExportDiscountIcon = this.currencies.get(good.ExportSymbol).Icon;
    }

    this.selected.discount.Details.push(record);
    detail.records.push(record);

    if (!this.selected.details.contains(detail)) this.selected.details.push(detail);

    let index = this.goods.pageIndex;
    this.goods.source = this.goods.source.filter(i => i != good);
    this.goods.pageIndex = index;

    this.edited = true;
  }

  public remove(records: DiscountDetailRecord[]) {

    records.map(i => i).forEach(record => {

      let detail: Group = this.selected.details.first(i => i.records.contains(record));

      detail.records.remove(record);
      if (detail.records.length < 1) this.selected.details.remove(detail);

      this.selected.discount.Details.remove(record);

      let good = lu.goods.get(record.ID);

      let index = this.goods.pageIndex;
      this.goods.source = this.goods.source.union([good]);
      this.goods.pageIndex = index;

      this.edited = true;
    });
  }

  public save() {

    this.ds.discounts(this.selected.discount.ID > 0 ? 'update' : 'create', this.selected.discount).subscribe(discounts => {

      if (discounts && discounts.length == 1) {

        this.open(discounts[0]);
        this.edited = false;
      }
    });
  }

  public cancel() {

    if (this.selected.discount.ID == 0 && this.selected.discount.Details.length == 0) {
      this.edited = false;
      this.close();
      return;
    }

    this.mb.confirms("discourage", "Bạn muốn hủy đơn trừ hàng lỗi đang mở?",
      () => this.ds.discounts('cancel', this.selected.discount).pipe(map(invoice => invoice
        ? commit<boolean>(() => {
          this.edited = false;
          this.close();
        }).return(true)
        : false)));
  }

  public confirm() {

    this.mb.confirms('encourage', "Bạn muốn xác nhận trừ hàng lỗi đang mở?",
      () => this.ds.discounts('confirm', this.selected.discount).pipe(map(discounts => discounts && discounts.length == 1
        ? commit<boolean>(() => {
          this.discounts.source = this.discounts.source.filter(i => i.ID != this.selected.discount.ID).union(discounts);
          this.close();
        }).return(true)
        : false)));
  }

}
